/**
 * Field paths verified by Fable against live d6d88aea engine-outputs.
 * List-total for advances is total_cents. Per-line key is amount_cents — do not use that as the total.
 */

export type WaterfallStep = {
  step?: string | number;
  description?: string;
  deduction_amount?: number | string;
  running_balance?: number | string;
  notes?: string[] | string;
};

export type PolicyDeductions = {
  gross_loss?: number | string;
  total_deductions?: number | string;
  net_claim?: number | string;
  deduction_waterfall?: WaterfallStep[];
  coinsurance?: Record<string, unknown>;
  notes?: string[];
  /** Engine-review stale flag. Badge only — do not hide N. */
  stale?: boolean;
};

export type PolicyFacts = {
  form?: string;
  bi_definition?: string;
  extra_expense?: string;
  period_of_restoration?: string;
  waiting_period_hours?: number | string;
  waiting_period_retroactive?: boolean;
  coinsurance_pct?: number | string;
  actual_coverage_limit?: number | string;
  annual_bi_value?: number | string;
  policy_limit?: number | string;
  dollar_deductible?: number | string;
  adv_deductible_days?: number | string;
  epi_included?: boolean;
  civil_authority?: string;
  service_interruption?: string;
  ordinance_or_law?: string;
  [key: string]: unknown;
};

export type OffsetRow = {
  key: string;
  label: string;
  amount?: number | string;
  source: string;
};

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function unwrapOutput(v: unknown): Record<string, unknown> | null {
  const obj = asObj(v);
  if (!obj) return null;
  return asObj(obj.outputs) || asObj(obj.output) || obj;
}

function looksLikePolicyDeductions(v: Record<string, unknown> | null): boolean {
  if (!v) return false;
  return 'net_claim' in v || 'gross_loss' in v || Array.isArray(v.deduction_waterfall);
}

export function pickPolicyDeductions(source: unknown): PolicyDeductions | null {
  const root = asObj(source);
  if (!root) return null;

  const candidates: unknown[] = [
    root.policy_deductions,
    asObj(root.engine_outputs)?.policy_deductions,
    asObj(root.engines)?.policy_deductions,
    root.outputs,
    root.output,
    root,
  ];

  for (const c of candidates) {
    const inner = unwrapOutput(c);
    if (looksLikePolicyDeductions(inner)) {
      const stale = Boolean(
        inner.stale ||
          root.stale ||
          root.policy_deductions_stale ||
          asObj(c)?.stale,
      );
      return { ...(inner as PolicyDeductions), stale };
    }
  }
  return null;
}

export function isPolicyDeductionsSkipped(pd: PolicyDeductions | null): boolean {
  if (!pd) return true;
  const notes = Array.isArray(pd.notes) ? pd.notes.map(String).join(' ') : String(pd.notes || '');
  if (/skipped/i.test(notes) || /no gross loss/i.test(notes)) return true;
  const steps = Array.isArray(pd.deduction_waterfall) ? pd.deduction_waterfall : [];
  const gross = Number(pd.gross_loss);
  if (steps.length === 0 && Number.isFinite(gross) && gross === 0) return true;
  return false;
}

export function pickPolicyFacts(source: unknown): PolicyFacts | null {
  const root = asObj(source);
  if (!root) return null;
  return (
    asObj(root.policy_terms) ||
    asObj(root.policy_facts) ||
    asObj(asObj(root.canonical)?.policy_terms) ||
    asObj(asObj(root.engine_outputs)?.policy_terms)
  ) as PolicyFacts | null;
}

function num(v: unknown): number | string | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v === 'number' || typeof v === 'string') return v;
  return undefined;
}

function pickAdvanceTotal(source: Record<string, unknown>): number | string | undefined {
  const blob =
    unwrapOutput(source.carrier_advances) ||
    unwrapOutput(asObj(source.engine_outputs)?.carrier_advances) ||
    null;
  if (!blob) return undefined;
  if (blob.total_cents === undefined || blob.total_cents === null || blob.total_cents === '') {
    return undefined;
  }
  const cents = Number(blob.total_cents);
  return Number.isFinite(cents) ? cents / 100 : undefined;
}

export function pickOffsetRows(source: unknown): OffsetRow[] {
  const root = asObj(source) || {};
  const bag = unwrapOutput(root.engine_outputs) || unwrapOutput(root.outputs) || root;
  const mitigation =
    unwrapOutput(root.mitigation) || unwrapOutput(bag.mitigation) || {};
  const catchUp =
    unwrapOutput(root.catch_up_sales) || unwrapOutput(bag.catch_up_sales) || {};

  const specs: Array<{ key: string; label: string; raw: unknown; source: string }> = [
    {
      key: 'mitigation',
      label: 'Mitigation credit',
      raw: mitigation.mitigation_credit_amount,
      source: 'mitigation.mitigation_credit_amount',
    },
    {
      key: 'catch_up',
      label: 'Catch-up / delayed sales',
      raw: catchUp.total_catch_up_offset,
      source: 'catch_up_sales.total_catch_up_offset',
    },
    {
      key: 'advances',
      label: 'Carrier advances',
      raw: pickAdvanceTotal(root),
      source: 'carrier_advances.total_cents',
    },
  ];

  return specs
    .filter((s) => s.raw !== undefined && s.raw !== null && s.raw !== '')
    .map((s) => ({
      key: s.key,
      label: s.label,
      amount: num(s.raw),
      source: s.source,
    }));
}

export function identityHolds(pd: PolicyDeductions | null): boolean | null {
  if (!pd || isPolicyDeductionsSkipped(pd)) return null;
  const gross = Number(pd.gross_loss);
  const deductions = Number(pd.total_deductions);
  const net = Number(pd.net_claim);
  if (![gross, deductions, net].every(Number.isFinite)) return null;
  return Math.abs(gross - deductions - net) < 0.015;
}
